import React, { useState, useEffect, ReactElement } from 'react';
import { useDispatch } from 'react-redux'
import type { Dispatch } from 'redux'
import Moment from 'moment';
import { DataTable } from 'primereact/datatable';
import { Paginator } from 'primereact/paginator';
import { Column } from 'primereact/column';
import { actions as baselineTaskActions } from '../../../actions/baselineTaskActions';
import * as types from '../../../constants/actionTypes';
import { BaselineTaskItem } from '../../../types/common';
import Notification from '../../Common/Notification';
import { DEFAULT_PAGE_SIZE, DATE_FORMAT } from '../../../constants/constants';
import { daysToDaysWeeks } from '../../../helpers/dateTime';
import { getDataTableSortFields, getSearchParamSortFields } from '../../../helpers/sortHelper';
import './ReviewImportedTasks.scss';

interface ReviewImportedTasksProps {
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
    baselineId: number;
}

interface SearchParams {
    baselineId: number,
    pageSize: number,
    pageNumber: number,
    sortBy: string,
    sortDir: string,
    multiColumnSort: Array<{ [key: string]: string }>
}

const ReviewImportedTasks = ({ setIsLoading, baselineId }: ReviewImportedTasksProps): ReactElement => {

    const dispatch: Dispatch = useDispatch();
    const searchBaselineTasks = async (request) => dispatch(baselineTaskActions.searchBaselineTasks(request));

    const [searchParams, setSearchParams] = useState<SearchParams>({
        baselineId: baselineId,
        pageSize: DEFAULT_PAGE_SIZE,
        pageNumber: 1,
        sortBy: "",     // Blank by default so the DataTable performs no sorting of its own, which means multiColumnSort's influence on the API will remain unchanged
        sortDir: "",
        multiColumnSort: [
            {
                sortBy: "targetDaysOut",
                sortDir: "desc"
            },
            {
                sortBy: "targetStaticDate",
                sortDir: "asc"
            },
            {
                sortBy: "ordinal",
                sortDir: "asc"
            }
        ]
    });
    const [baselineTasks, setBaselineTasks] = useState<Array<BaselineTaskItem>>([]);
    const [totalCount, setTotalCount] = useState<number>(0);

    useEffect(() => {
        search(searchParams);
    }, []);

    const search = async (params: SearchParams) => {

        setIsLoading(true);

        try {
            const result = await searchBaselineTasks(params);
            const payload = result.payload;
            if (result.type === types.SEARCH_BASELINE_TASK_SUCCESS) {
                const tasks = payload.results.map((item) => { return { ...item, IsChecked: false } });
                setBaselineTasks(tasks);
                setTotalCount(payload.totalCount);
            }
            else if (result.type === types.SEARCH_BASELINE_TASK_FAILURE) {
                Notification("error", "An Error Occurred", payload.message)
            }
        } catch (error) {
            Notification("error", "Error", error);
        } finally {
            setIsLoading(false);
        }
    }

    const nameTemplate = (rowData) => {
        return (
            <div className="show-newlines task-name">{rowData.name}</div>
        );
    }

    const startDaysOutTemplate = (rowData) => {
        return daysToDaysWeeks(rowData.startDaysOut);
    }

    const startStaticDateTemplate = (rowData) => {
        if (rowData.startStaticDate) {
            return Moment.utc(rowData.startStaticDate).format(DATE_FORMAT);
        }

        return "";
    }

    const targetDaysOutTemplate = (rowData) => {
        return daysToDaysWeeks(rowData.targetDaysOut);
    }

    const targetStaticDateTemplate = (rowData) => {
        if (rowData.targetStaticDate) {
            return Moment.utc(rowData.targetStaticDate).format(DATE_FORMAT);
        } else {
            return null;
        }
    }

    const onSort = async ({ sortField, sortOrder }) => {
        const { sortBy, sortDir } = getSearchParamSortFields(sortField, sortOrder);

        // Only search again if the values have changed
        if (searchParams.sortBy !== sortBy || searchParams.sortDir !== sortDir) {

            // Once a column has been clicked, the default multi-column sorting is no longer valid, so clear it out
            const updatedParams = {
                ...searchParams,
                sortBy: sortBy,
                sortDir: sortDir,
                multiColumnSort: []
            }

            setSearchParams(updatedParams);
            await search(updatedParams);
        }
    }

    const updateParamsAndSearch = async (name, value) => {
        if (name) {

            const updatedParams = {
                ...searchParams, [name]: value
            }

            setSearchParams(updatedParams);
            await search(updatedParams);
        } else {
            // If no params passed in, just search
            await search(searchParams);
        }
    }

    const onPageChange = async (e) => {
        await updateParamsAndSearch('pageNumber', e.page + 1);
    }

    return (
        <div className="review-imported-tasks-content">

            <div className="wizard-content-title">
                {"Take a moment to review your imported Task Lists, if everything looks good, let's proceed to the next step."}
            </div>

            <div className="task-list-table">
                <DataTable value={baselineTasks}
                    filterDisplay="row"
                    sortField={getDataTableSortFields(searchParams).sortField}
                    sortOrder={getDataTableSortFields(searchParams).sortOrder}
                    onSort={onSort}
                    className="p-datatable-sm p-datatable-striped">
                    <Column key="name" field="name" header="Task Name" sortable={true} body={nameTemplate} className='data-column' headerClassName='data-header'></Column>
                    <Column key="startDaysOut" field="startDaysOut" header="Relative Start" sortable={true} body={startDaysOutTemplate} className='data-column' headerClassName='data-header'></Column>
                    <Column key="targetDaysOut" field="targetDaysOut" header="Relative Target" sortable={true} body={targetDaysOutTemplate} className='data-column' headerClassName='data-header'></Column>
                    <Column key="startStaticDate" field="startStaticDate" header="Static Start" sortable={true} body={startStaticDateTemplate} className='data-column' headerClassName='data-header'></Column>
                    <Column key="targetStaticDate" field="targetStaticDate" header="Static Target" sortable={true} body={targetStaticDateTemplate} className='data-column' headerClassName='data-header'></Column>
                    <Column key="baselineJobTitle" field="baselineJobTitle" header="Baseline Job Title" sortable={true} className='data-column' headerClassName='data-header'></Column>
                    <Column key="department" field="department" header="Department" sortable={true} className='data-column' headerClassName='data-header'></Column>
                </DataTable>

                {
                    totalCount > searchParams.pageSize &&
                    <Paginator
                        leftContent={<span>Rows per page: </span>}
                        rows={searchParams.pageSize}
                        totalRecords={totalCount}
                        first={(searchParams.pageNumber - 1) * searchParams.pageSize}
                        rowsPerPageOptions={[20, 50, 100]}
                        onPageChange={onPageChange}
                        template='RowsPerPageDropdown CurrentPageReport FirstPageLink PrevPageLink NextPageLink LastPageLink'
                        currentPageReportTemplate='{first}-{last} of {totalRecords}' />
                }
            </div>
        </div>
    );
}

export default ReviewImportedTasks;
